import React, { useEffect, useState } from 'react';
import { Alert, Button, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi, getCompanyId } from './subscriptionApi';

// Dashboard subscription notice. Self-contained so it can be dropped into the
// existing Dashboard without touching its logic.
//
// Special rule: when a queued plan already exists we do NOT show the
// "your plan expires on this date" warning — the next plan auto-activates.
export default function SubscriptionBanner() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const companyId = getCompanyId();
    if (!companyId) return;
    subscriptionApi
      .getStatus(companyId)
      .then((res) => setStatus(res.data))
      .catch((err) => console.error('Subscription status failed', err));
  }, []);

  if (!status) return null;

  // Expired → hard renew prompt.
  if (status.isExpired) {
    return (
      <Box sx={{ mb: 2 }}>
        <Alert
          severity='error'
          action={
            <Button
              color='inherit'
              size='small'
              onClick={() => navigate('/admin/Subscription')}>
              Renew Now
            </Button>
          }>
          {status.message || 'Subscription Expired. Please Renew Your Plan.'}
        </Alert>
      </Box>
    );
  }

  // A plan is already queued → reassure, don't nag about the expiry date.
  if (status.hasQueuedPlan) {
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity='success'>
          Your next plan <strong>{status.queuedPlanName}</strong> is purchased
          and will activate automatically when the current plan ends.
        </Alert>
      </Box>
    );
  }

  // Active and no queued plan → show expiry date, and warn when near expiry.
  if (status.hasSubscription && status.showExpiryWarning) {
    const near = status.daysRemaining <= 7;
    return (
      <Box sx={{ mb: 2 }}>
        <Alert
          severity={near ? 'warning' : 'info'}
          action={
            near ? (
              <Button
                color='inherit'
                size='small'
                onClick={() => navigate('/admin/Subscription')}>
                Renew
              </Button>
            ) : null
          }>
          Your <strong>{status.currentPlanName}</strong> plan expires on{' '}
          <strong>
            {status.endDate
              ? new Date(status.endDate).toLocaleDateString('en-GB')
              : ''}
          </strong>{' '}
          ({status.daysRemaining} day{status.daysRemaining === 1 ? '' : 's'}{' '}
          left).
        </Alert>
      </Box>
    );
  }

  return null;
}
